import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { ChangesStack, Device, SaveDeviceOptions } from './device.interface';
import { TranslateService } from '@ngx-translate/core';
import * as lodash from 'lodash-es';
import { CommonService } from 'src/app/shared/services/common.service';
import moment from 'moment';
import { RejectedLogsFilterForm } from './rejection-devices-connection/rejected-devices-connection.interface';
import { DeviceConstantsService } from './device-constants.service';

@Injectable({
	providedIn: 'root'
})
export class DevicesService {
	private readonly ROUTE = 'devices';
	private currentDevice: Device | null = null;
	public changeStack: ChangesStack | null = null;

	waitTimeout = 45;
	deviceWarranty = 1; //In years

	constructor(
		private http: HttpClient,
		private translate: TranslateService,
		private devicesConstantsService: DeviceConstantsService,
	) { }

	addDevices(device: Device) {
		return this.http.post(`/${this.ROUTE}/add-device`, { ...device }, {
			observe: 'body'
		});
	}

	updateDevice(device: Device, options?: SaveDeviceOptions) {
		return this.http.post(`/${this.ROUTE}/update-device`, { device, options }, {
			observe: 'body'
		});
	}

	list(criteria?: {
		search_by?: string;
		is_partial?: boolean;
		is_deleted?: boolean;
		source?: string;
		keyword?: string;
		is_bwt_devices_only?: boolean;
		get_changes_stack?: boolean;
		stage?: number[];
	}) {
		return this.http.get(`/${this.ROUTE}/search-devices`, { params: criteria });
	}

	getDevicesInfo(imeis: string[]) {
		return this.http.post(`/${this.ROUTE}/get-devices-info`, { imeis });
	}

	/**
	 * True when the IMEI belongs to a device that exists. The device-page route guard uses it to
	 * choose between rendering the page and falling through to the 404 route.
	 *
	 * `source` defaults to the normal search (which excludes RMA/Discard, like every other device
	 * screen) — the guard passes `'device-page'` only for the RMA/Discard stage slugs, so opening
	 * an RMA-stage device's page from the RMA Devices list doesn't 404 (VMSPRO-530).
	 *
	 * A failed request resolves to true on purpose: a network or server error must not be shown to
	 * the user as "this device does not exist".
	 */
	deviceExistsByImei(imei: string, source: string = 'search'): Observable<boolean> {
		return this.list({ search_by: 'imei', is_deleted: false, source, keyword: imei }).pipe(
			map((res: { data?: { devices?: Device[] } }) => {
				// Only a well-formed answer is allowed to say "no such device". A reply without a
				// device list tells us nothing, so treat it as found rather than 404 a real device.
				const devices = res?.data?.devices;
				return !Array.isArray(devices) || devices.length > 0;
			}),
			catchError(() => of(true))
		);
	}

	delete(deviceId: string) {
		return this.http.delete(`/${this.ROUTE}/delete-device/${deviceId}`);
	}

	sendCommand(imei: string, command: string) {
		return this.http.post(`/${this.ROUTE}/send-command`, { imei, command });
	}

	resetDeviceData(imei: string) {
		return this.http.post(`/${this.ROUTE}/reset-device-data`, { imei });
	}

	refetchDeviceInfo( imei: string, type: 'system' | 'cell') {
		return this.http.post(`/${this.ROUTE}/refetch-device-info`, { imei, type });
	}

	pushFw(imei: string, fw: string) {
		return this.http.post(`/${this.ROUTE}/push-firmware`, { imei, fw });
	}

	remove(deviceId: string) {
		return this.http.post(`/${this.ROUTE}/removeDevice`, { deviceId });
	}

	moveToStage(deviceId: string, stage: number) {
		return this.http.post(`/${this.ROUTE}/move-to-stage`, { deviceId, stage });
	}

	assignDevices(devices: Device[]) {
		return this.http.post(`/${this.ROUTE}/assignDevices`, { devices }, {
			observe: 'body'
		});
	}

	getDebugRecords(imei: string, fromDate?: number, toDate?: number) {
		const params = { from: fromDate, to: toDate };
		return this.http.get(`/${this.ROUTE}/get-device-debug-records/${imei}`, { params });
	}

	getRestartCountHistory(imei: string, fromDate?: number, toDate?: number) {
		const params = { from: fromDate, to: toDate };
		return this.http.get(`/${this.ROUTE}/restart-count-history/${imei}`, { params });
	}

	getRestartCountReport(searchMode: 'gnss_off' | 'restart_range', from?: number, to?: number) {
		const params: { search_mode: string, from?: number, to?: number } = { search_mode: searchMode };

		if (searchMode == 'restart_range') {
			params.from = from;
			params.to = to;
		}

		return this.http.get(`/${this.ROUTE}/restart-count-report`, { params });
	}

	getBwtConnectivityReport() {
		return this.http.get(`/${this.ROUTE}/bwt-connectivity-report`);
	}

	getConnectivityTrack(imei: string, fromDate?: number, toDate?: number) {
		const params = { imei, fromDate, toDate };
		return this.http.get(`/connectivity-track`, { params });
	}

	getConfigTrack(params: { imei: string, startDate: number, endDate: number }) {
		return this.http.get(`/config-track`, { params });
	}

	getDefineLog(params: { [param: string]: string | number }) {
		return this.http.get(`/define-log`, { params });
	}

	mapCellRssi(cellRssi: number) {
		let result = '';
		switch (true) {
			case cellRssi == 0:
				result = this.translate.instant('cellular_rssi_values.zero_cellular_rssi');
				break;
			case cellRssi == 1:
				result = this.translate.instant('cellular_rssi_values.one_cellular_rssi');
				break;
			case cellRssi >= 2 && cellRssi <= 30:
				result = ((Math.abs(cellRssi) - 30) * 2 - 53) + " dBm";
				break;
			case cellRssi == 31:
				result = this.translate.instant('cellular_rssi_values.mid_cellular_rssi');
				break;
			case cellRssi > 31 && cellRssi <= 98:
				result = this.translate.instant('cellular_rssi_values.unknown_cellular_rssi', { cellRssi });
				break;
			case cellRssi == 99:
				result = this.translate.instant('cellular_rssi_values.cellular_rssi_unknown_or_undetectable');
				break;
			default:
				break;
		}

		return result;
	}

	mapCycleState(cycleState: number) {
		let result = '';
		switch (cycleState) {
			case 1:
				result = this.translate.instant('cycle_state_values.idle');
				break;
			case 2:
				result = this.translate.instant('cycle_state_values.charge');
				break;
			case 3:
				result = this.translate.instant('cycle_state_values.cooling_down');
				break;
			case 4:
				result = this.translate.instant('cycle_state_values.cooldown_done');
				break;
			default:
				break;
		}
		return result;
	}

	checkFieldsFwSupported(field: string, deviceFW: string): boolean {
		if (!deviceFW)
			return false;

		let isSupported = true;

		switch(field) {
			case 'volt_samples':
			case 'volt_samples_period':
			case 'el_sampling_period':
				isSupported = this.isFwVersionGreaterOrEqual(deviceFW, '0.2.2');
				break;
			case 'temperature_sensor_enable':
				isSupported = this.isFwVersionGreaterOrEqual(deviceFW, '0.1.5');
				break;
			case 'enable_state_change_log':
				isSupported = this.isFwVersionGreaterOrEqual(deviceFW, '1.1.3');
				break;
			default:
				return false;
		}

		return isSupported;
	}

	isFwVersionGreaterOrEqual(deviceFW: string, targetFW: string): boolean {
		const a = deviceFW.split('.').map(Number);
		const b = targetFW.split('.').map(Number);

		for (let i = 0; i < Math.max(a.length, b.length); i++) {
			const numA = a[i] || 0;
			const numB = b[i] || 0;
			if (numA > numB) return true;
			if (numA < numB) return false;
		}
		return true;
	}

	getDeviceStageLabel(deviceStage: number) {
		const stageLabel = this.devicesConstantsService.deviceStageOptions.find(stage => stage.value === deviceStage)?.label;
		return this.translate.instant(stageLabel);
	}

	setCurrentDevice(device: Device, changeStack: ChangesStack = null) {
		this.currentDevice = device;
		this.changeStack = changeStack;
	}

	getCurrentDevice(): Device | null {
		return this.currentDevice;
	}

	getChangeStack(): ChangesStack | null {
		return this.changeStack;
	}

	getFirmwareVersions() {
		return this.http.get(`FW/versions`);
	}

	saveFirmwareFiles(name: string, file: File) {
		const formData = new FormData();
		formData.append('name', name);
		formData.append('file', file);

		return this.http.post(`FW/save-firmware-files`, formData, {
			observe: 'body',
		});
	}

	deleteFirmwareVersion(version: string) {
		return this.http.delete(`FW/delete-version/${version}`, {
			observe: 'body'
		});
	}

	getRejectedDevicesConnectionLogs(criteria: RejectedLogsFilterForm) {
		const queryParams: { [key: string]: string | number | boolean } = {};
		for (const key in criteria) {
			const value = criteria[key as keyof RejectedLogsFilterForm];
			if (value && value !== null)
				queryParams[key] = value;
		}

		return this.http.get(`/api/rejected-devices-connection/get-rejected-devices-logs`, {
			params: queryParams 
		});
	}

	deleteRejectedDeviceConnectionById(id: number) {
		return this.http.delete(`/api/rejected-devices-connection/${id}`, {
			observe: 'body'
		});
	}

	getDeviceCellularInfoTrack(params: { imei: string, startDate: number, endDate: number }) {
		return this.http.get(`/api/device-info-tracking/get-cellular-track`, { params });
	}

	getDeviceSystemInfoTrack(params: { imei: string, startDate: number, endDate: number }) {
		return this.http.get(`/api/device-info-tracking/get-system-info-track`, { params });
	}

	pushFwPerDeviceCategory(devicesCategory: number, firmwareVersion: string) {
		return this.http.post(`/api/${this.ROUTE}/push-fw-devices-category`, { devicesCategory, firmwareVersion });
	}

	getStageFwHistory() {
		return this.http.get(`/api/${this.ROUTE}/stage-fw-update-history`);
	}

	getCmFirmwareVersions() {
		return this.http.get(`/api/${this.ROUTE}/cm-fw-versions`);
	}

	saveFwVersions(data: { module: string[]; quecpython_version: string[]; fw_version: string[] }) {
		return this.http.post(`/api/${this.ROUTE}/create-cm-fw-version`, data);
	}
}
